import type { ReactNode } from 'react';

interface SectionHeadProps {
  title: string;
  meta?: ReactNode;
}

export function SectionHead({ title, meta }: SectionHeadProps) {
  return (
    <div className="mb-[14px] flex items-baseline justify-between">
      <h2 className="font-heading text-[15px] font-semibold m-0 tracking-tight">{title}</h2>
      {meta ? (
        <span className="text-[11px] uppercase tracking-[0.14em] text-foreground/55">{meta}</span>
      ) : null}
    </div>
  );
}

export function TableWrap({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <table className="w-full border-collapse">{children}</table>
    </div>
  );
}

export function Th({
  children,
  align = 'left',
}: { children: ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={[
        'border-b border-border bg-background px-4 py-3',
        'text-[10px] font-semibold uppercase tracking-[0.16em] text-foreground/55',
        align === 'right' ? 'text-right' : 'text-left',
      ].join(' ')}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align = 'left',
  className,
}: { children: ReactNode; align?: 'left' | 'right'; className?: string }) {
  return (
    <td
      className={[
        'border-b border-border/50 px-4 py-[14px] text-[13px] align-middle',
        align === 'right' ? 'text-right' : 'text-left',
        'last-row:border-b-0',
        className ?? '',
      ].join(' ')}
    >
      {children}
    </td>
  );
}

export function Tr({
  children,
  hoverable = true,
  className,
}: { children: ReactNode; hoverable?: boolean; className?: string }) {
  return (
    <tr
      className={[
        hoverable ? 'hover:bg-secondary/35 transition-colors duration-150' : '',
        '[&:last-child>td]:border-b-0',
        className ?? '',
      ].join(' ')}
    >
      {children}
    </tr>
  );
}

export function Pill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-block rounded px-2 py-[3px] text-[11px] font-medium bg-secondary text-foreground/75 tracking-[0.01em]">
      {children}
    </span>
  );
}

export function StatusDot({ kind }: { kind: 'live' | 'draft' | 'paused' | 'sunset' | 'neutral' }) {
  const cls =
    kind === 'live'
      ? 'bg-[#2f7d4f]'
      : kind === 'paused'
        ? 'bg-gold'
        : kind === 'sunset'
          ? 'bg-destructive'
          : 'bg-foreground/35';
  return <span className={`inline-block h-[6px] w-[6px] rounded-full ${cls} flex-shrink-0`} />;
}

export function StatusLabel({
  status,
}: { status: 'live' | 'draft' | 'paused' | 'sunset' | string }) {
  const kind =
    status === 'live' || status === 'draft' || status === 'paused' || status === 'sunset'
      ? status
      : 'neutral';
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span className="inline-flex items-center gap-[6px] text-[12px]">
      <StatusDot kind={kind as 'live' | 'draft' | 'paused' | 'sunset' | 'neutral'} />
      {label}
    </span>
  );
}

export function StatusBar({ left, right }: { left: ReactNode; right: ReactNode }) {
  return (
    <div className="mt-6 flex justify-between border-t border-border pt-[14px] text-[11px] text-foreground/50">
      <span>{left}</span>
      <span className="font-mono">{right}</span>
    </div>
  );
}
